import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Loader2, Wand2, Save, RotateCcw, ExternalLink, Upload, AlertTriangle, CheckCircle2, FileText, MessageSquare, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PSYCHOLOGY_TENETS = [
  {
    icon: "🕳️",
    title: "Investors look for reasons to say NO",
    body: "They see 500 decks a quarter and fund 5. Their default is pass. Your deck's job is to remove every excuse to pass, not to make them love you.",
  },
  {
    icon: "⏱️",
    title: "They have 2-3 minutes for your deck",
    body: "First skim on the phone. If they don't understand the ask + market + team in 90 seconds, you're out. Compress. Every slide must earn the next.",
  },
  {
    icon: "🎯",
    title: "They pattern-match on 3 things",
    body: "Team pedigree, market timing, defensibility. Not science elegance. If any of the three is weak, the other two must be exceptional AND obvious in the first 3 slides.",
  },
  {
    icon: "🤝",
    title: "They fund PEOPLE first, deals second",
    body: "The most important slide is often the team slide. Names, prior INDs, exits, prior companies. If you don't have this, get advisors + board with it before you raise.",
  },
  {
    icon: "💧",
    title: "They diligence what you DON'T say",
    body: "Any gap you hide gets found in reference calls. Name your weakness before they do — and show the plan to close it. Founders who volunteer risks build trust; those who bury them lose it.",
  },
];

const SELF_CHECK_QUESTIONS = [
  { field: "reasons_to_say_no",         label: "Top 3 reasons a good investor would pass on this deal",     placeholder: "Be brutal. Team gap, market size, moat, timing, science risk." },
  { field: "one_slide_answer",          label: "If you had ONE slide, what would it say (≤30 words)?",       placeholder: "The one thing that MUST land. Everything else is optional." },
  { field: "worst_question",            label: "The single hardest question they'll ask — and your answer",  placeholder: "'How is this different from Sarepta?' → 2-sentence answer." },
  { field: "biggest_lie_by_omission",   label: "What are you NOT saying that could hurt you in diligence?",   placeholder: "Say it now. It will come out in reference calls anyway." },
  { field: "why_this_investor",         label: "Why THIS specific investor archetype (not 'anyone with £2M')",placeholder: "Thesis fit, portfolio adjacency, board experience needed, network." },
];

const SEV_STYLE = {
  high:   "border-red-200 bg-red-50 text-red-900",
  medium: "border-amber-200 bg-amber-50 text-amber-900",
  low:    "border-slate-200 bg-slate-50 text-slate-700",
};

// ---------- Section A · Investor psychology + self-check ----------
function PsychologySection({ selfCheck, setSelfCheck, persist }) {
  const [busyField, setBusyField] = useState(null);
  const update = (k, v) => setSelfCheck({ ...selfCheck, [k]: v });

  const draftAnswer = async (field, label) => {
    setBusyField(field);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/preparation/coach-answer`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ field, fieldLabel: label, current: selfCheck[field] || "" }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.answer) {
          const next = { ...selfCheck, [field]: d.answer };
          setSelfCheck(next);
          persist({ self_check: next });
          toast.success("AI coach draft ready");
        } else toast.info("No draft");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusyField(null);
  };

  return (
    <div className="space-y-4" data-testid="prep-psychology">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Brain className="w-4 h-4 text-teal-600" /> How investors actually think</CardTitle>
          <p className="text-xs text-slate-500">Five uncomfortable truths every biotech founder should internalise before their next pitch.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PSYCHOLOGY_TENETS.map((t, i) => (
            <div key={i} className="border rounded-lg bg-slate-50/40 p-3" data-testid={`prep-tenet-${i}`}>
              <div className="flex items-start gap-2">
                <span className="text-lg">{t.icon}</span>
                <div>
                  <p className="text-sm font-medium text-slate-900 leading-snug">{t.title}</p>
                  <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{t.body}</p>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-800">Your 5-question honesty check</CardTitle>
          <p className="text-xs text-slate-500">Write it down before you take the meeting. The AI coach can rough-draft each — you sharpen.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {SELF_CHECK_QUESTIONS.map((q) => (
            <div key={q.field}>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-slate-800">{q.label}</Label>
                <Button size="sm" variant="ghost" onClick={() => draftAnswer(q.field, q.label)} disabled={busyField === q.field} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`prep-sc-wand-${q.field}`}>
                  {busyField === q.field ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  <span className="ml-1 text-xs">AI draft</span>
                </Button>
              </div>
              <AutoResizeTextarea
                value={selfCheck[q.field] || ""}
                onChange={(e) => update(q.field, e.target.value)}
                onBlur={() => persist({ self_check: selfCheck })}
                minRows={2}
                maxHeight={400}
                className="mt-1 text-sm leading-relaxed"
                placeholder={q.placeholder}
                data-testid={`prep-sc-${q.field}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Section B · Messaging summary (read-only mirror of Framing) ----------
function MessagingSection() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/framing-summary`, { credentials: "include" });
      if (res.ok) setSummary(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  if (loading) return <Card><CardContent className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></CardContent></Card>;
  const hasFraming = summary?.has_any_framing;

  if (!hasFraming) {
    return (
      <Card className="border-teal-200 bg-teal-50/40" data-testid="prep-msg-empty">
        <CardContent className="py-6 text-center space-y-2">
          <MessageSquare className="w-8 h-8 text-teal-500 mx-auto" />
          <p className="text-sm text-slate-700">No messaging drafted yet.</p>
          <Link to="/strategy/framing?tab=narrative">
            <Button variant="outline" size="sm" data-testid="prep-msg-goto">
              Open Framing &amp; Storytelling <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const inv = summary.investor_message || {};
  const pha = summary.pharma_message || {};
  const chip = (val) => val ? val : <span className="text-slate-300 italic">not drafted</span>;

  return (
    <div className="space-y-3" data-testid="prep-messaging">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-teal-600" /> Your messaging (from Framing)</CardTitle>
            <Link to="/strategy/framing?tab=narrative">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-teal-600" data-testid="prep-msg-edit">
                Edit in Framing <ExternalLink className="w-3 h-3 ml-1.5" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Core message</Label>
            <p className="text-sm text-slate-800 leading-relaxed mt-0.5" data-testid="prep-msg-core">{chip(summary.core_message)}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t pt-3">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase tracking-wide">4-6 words</Label>
              <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="prep-msg-4to6">{chip(summary.four_to_six_words)}</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] text-slate-500 uppercase tracking-wide">One sentence</Label>
              <p className="text-sm text-slate-800 mt-0.5" data-testid="prep-msg-1sent">{chip(summary.one_sentence)}</p>
            </div>
          </div>
          <div className="border-t pt-3">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">One paragraph</Label>
            <p className="text-sm text-slate-800 leading-relaxed mt-0.5" data-testid="prep-msg-1para">{chip(summary.one_paragraph)}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-l-4 border-l-amber-500" data-testid="prep-msg-investor">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm text-slate-800">Investor pitch</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-xs">
            {["hook", "promise", "proof", "ask"].map((k) => (
              <div key={k}>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wide">{k}</Label>
                <p className="text-slate-800 mt-0.5">{chip(inv[k])}</p>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500" data-testid="prep-msg-pharma">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm text-slate-800">Pharma pitch</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-xs">
            {["hook", "promise", "proof", "ask"].map((k) => (
              <div key={k}>
                <Label className="text-[10px] text-slate-500 uppercase tracking-wide">{k}</Label>
                <p className="text-slate-800 mt-0.5">{chip(pha[k])}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------- Section C · Deck review ----------
function DeckReviewSection({ deckReview, setDeckReview, persist }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/preparation/deck-review`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      if (res.status === 404) {
        toast.error("Upload a pitch deck on the Profile page first.");
      } else if (res.ok) {
        const d = await res.json();
        setDeckReview(d);
        persist({ deck_review: d });
        toast.success(`Reviewed ${d.deck_filename || "deck"} — ${d.critiques.length} issues found`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed");
      }
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const clear = () => { setDeckReview(null); persist({ deck_review: null }); };

  return (
    <Card data-testid="prep-deck">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><FileText className="w-4 h-4 text-teal-600" /> Deck review</CardTitle>
          <div className="flex items-center gap-1">
            {deckReview && (
              <Button size="sm" variant="ghost" onClick={clear} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="prep-deck-clear">
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" onClick={run} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-3" data-testid="prep-deck-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
              <span className="text-xs">{deckReview ? "Re-review" : "Review my deck"}</span>
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">Uses your most-recent pitch deck (Profile → Upload deck). AI acts as a senior biotech investor and lists concrete fixes.</p>
      </CardHeader>
      <CardContent>
        {!deckReview && (
          <div className="border rounded-lg border-dashed border-slate-300 p-6 text-center bg-slate-50/40">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">No review yet.</p>
            <p className="text-xs text-slate-400 mt-1">Click &quot;Review my deck&quot; to get a slide-by-slide critique with concrete fixes.</p>
          </div>
        )}
        {deckReview && (
          <div className="space-y-3">
            {deckReview.deck_filename && (
              <p className="text-xs text-slate-500">Reviewed: <span className="font-medium text-slate-700">{deckReview.deck_filename}</span> · {new Date(deckReview.generated_at).toLocaleString()}</p>
            )}
            {deckReview.global_notes && (
              <div className="bg-teal-50 border border-teal-200 rounded p-3 text-sm text-teal-900" data-testid="prep-deck-global">
                <p className="font-medium mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Overall verdict</p>
                <p className="leading-relaxed">{deckReview.global_notes}</p>
              </div>
            )}
            <div className="space-y-2">
              {deckReview.critiques.map((c) => (
                <div key={c.id} className={`border rounded-lg p-3 ${SEV_STYLE[c.severity] || SEV_STYLE.medium}`} data-testid={`prep-deck-cr-${c.id}`}>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="text-[10px] capitalize shrink-0">{c.severity}</Badge>
                    <span className="text-xs font-medium">{c.slide}</span>
                  </div>
                  <p className="text-sm mt-1"><span className="font-medium">Issue:</span> {c.issue}</p>
                  <p className="text-sm mt-1 flex items-start gap-1"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" /><span><span className="font-medium">Fix:</span> {c.fix}</span></p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Main page ----------
export default function FundraisingPreparation() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selfCheck, setSelfCheck] = useState({ why_this_investor: "", reasons_to_say_no: "", one_slide_answer: "", worst_question: "", biggest_lie_by_omission: "" });
  const [deckReview, setDeckReview] = useState(null);

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const me = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (me.ok) setUser(await me.json());
      const p = await fetch(`${BACKEND_URL}/api/fundraising/preparation/progress`, { credentials: "include" });
      if (p.ok) {
        const d = await p.json();
        setSelfCheck({ ...selfCheck, ...(d.self_check || {}) }); // eslint-disable-line
        setDeckReview(d.deck_review || null);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const persist = useCallback(async (overrides = {}, showToast = false) => {
    setSaving(true);
    try {
      const payload = { self_check: selfCheck, deck_review: deckReview, ...overrides };
      await fetch(`${BACKEND_URL}/api/fundraising/preparation/progress`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(payload),
      });
      if (showToast) toast.success("Saved");
    } catch (e) { if (showToast) toast.error("Save failed"); }
    setSaving(false);
  }, [selfCheck, deckReview]);

  if (loading) return <AppLayout user={user}><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div></AppLayout>;

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="fundraising-preparation-page">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-teal-600" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Preparation</h1>
              <p className="text-sm text-slate-500">Get ready for the investor conversation — the angle, the messaging, the deck.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => persist({}, true)} data-testid="prep-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>

        <div className="space-y-6">
          <PsychologySection selfCheck={selfCheck} setSelfCheck={setSelfCheck} persist={persist} />
          <MessagingSection />
          <DeckReviewSection deckReview={deckReview} setDeckReview={setDeckReview} persist={persist} />
        </div>
      </div>
    </AppLayout>
  );
}
